/* eslint-disable import/no-cycle */
import { Box } from '@chakra-ui/core';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomDropdownMenu, DropdownMenuItem } from '../../../../../../common';
import type { MultiChoiceOption } from '../../../../questionnaireTemplate.types';
import { QuestionnaireTemplateStore } from '../../../../store/questionnaireTemplateModel';
import { OptionMenuItem } from './OptionMenuItem';
import { VALIDATION_CONSTRAINTS } from './questionValidationRules.constraints';
import { OptionSelectionCount, ValidationOperator } from './questionValidationRules.types';
import { getOptionLabel } from './questionValidationRules.utils';

const EMPTY_OPTIONS: MultiChoiceOption[] = [];

interface QuestionValidationOptionSelectMenuProps {
  elementId: string;
  selectedOptionIds: string[];
  selectionCount: OptionSelectionCount;
  operator: ValidationOperator;
  onToggle: (optionId: string) => void;
  errorMessage: string | null;
}

export const QuestionValidationOptionSelectMenu = React.memo<QuestionValidationOptionSelectMenuProps>(
  function QuestionValidationOptionSelectMenu({
    elementId,
    selectedOptionIds,
    selectionCount,
    operator,
    onToggle,
    errorMessage,
  }) {
    const { t } = useTranslation('questionnaireTemplate');

    const options = QuestionnaireTemplateStore.useStoreState((s) => s.entities.elements[elementId]?.options ?? EMPTY_OPTIONS);

    const maxSelectable = useMemo(() => {
      const total = options.length;
      switch (operator) {
        case ValidationOperator.AT_LEAST:
          // All options are selectable — no upper bound beyond what exists
          return total;
        case ValidationOperator.DOES_NOT_HAVE_ANSWER:
          return VALIDATION_CONSTRAINTS.DOES_NOT_HAVE_ANSWER.maxAllowed(selectionCount, total);
        /* istanbul ignore next */
        case ValidationOperator.HAS_ANSWER:
          return VALIDATION_CONSTRAINTS.HAS_ANSWER.maxAllowed(selectionCount, total);
        case ValidationOperator.IS_EQUAL:
        default:
          return VALIDATION_CONSTRAINTS.IS_EQUAL.maxAllowed(selectionCount, total);
      }
    }, [operator, selectionCount, options.length]);

    const hasSelectionCapacity = selectedOptionIds.length < maxSelectable;

    const selectedLabels = useMemo(() => {
      return selectedOptionIds
        .map((id) => {
          const index = options.findIndex((o) => o.id === id);
          return index !== -1 ? { index, label: getOptionLabel(index) } : null;
        })
        .filter((item): item is { index: number; label: string } => item !== null)
        .sort((a, b) => a.index - b.index)
        .map((item) => item.label)
        .join(', ');
    }, [selectedOptionIds, options]);

    const dropdownItems: DropdownMenuItem[] = useMemo(
      () =>
        options.map((option, index) => ({
          id: option.id,
          label: getOptionLabel(index),
          description: option.text,
          isSelected: selectedOptionIds.includes(option.id),
        })),
      [options, selectedOptionIds],
    );

    const handleToggle = useCallback((optionId: string) => onToggle(optionId), [onToggle]);

    return (
      <CustomDropdownMenu
        label={
          selectedLabels
            ? t('formBuilder.canvas.element.validationRules.selectOptionWithSelection', { selectedOptions: selectedLabels })
            : t('formBuilder.canvas.element.validationRules.selectOptionNoSelection')
        }
        items={dropdownItems}
        maxHeight="300px"
        size="sm"
        isInvalid={!!errorMessage}
        renderItem={(item, index) => (
          <Box
            key={item.id}
            cursor="pointer"
            _hover={{ bg: 'gray.100' }}
            onClick={() => {
              if (item.isSelected) {
                handleToggle(item.id);
                return;
              }

              if (!hasSelectionCapacity) {
                if (maxSelectable === 1 && selectedOptionIds.length === 1) {
                  handleToggle(selectedOptionIds[0]);
                  handleToggle(item.id);
                }
                return;
              }
              handleToggle(item.id);
            }}
          >
            <OptionMenuItem
              index={index}
              isSelected={item.isSelected ?? /* istanbul ignore next */ false}
              isDisabled={!hasSelectionCapacity}
              selectionCount={selectionCount}
            />
          </Box>
        )}
      />
    );
  },
);
